import { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  BedDouble,
  FlaskConical,
  Scan,
  Pill,
  CreditCard,
  BarChart2,
  Users,
  Users2,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  Menu,
  MonitorSmartphone,
  Receipt,
  X,
  LifeBuoy,
  Printer,
  LayoutDashboard,
  IndianRupee,
  UserCircle,
  Scissors,
  Award,
  CalendarDays,
  ShoppingCart,
  Package,
  Calendar,
  ShieldCheck,
} from "lucide-react";
import { useAuthStore } from "@/store/auth.store";
import { authApi, patientApi } from "@/lib/api";
import { clsx } from "clsx";
import AppLogo from "../AppLogo";

const ALL_NAV = [
  {
    title: "EXECUTIVE",
    items: [
      {
        to: "/dashboard",
        icon: LayoutDashboard,
        label: "Dashboard",
        roles: [
          "owner",
          "hospital_admin",
          "doctor",
          "nurse",
          "receptionist",
          "lab_technician",
          "radiologist",
          "pharmacist",
          "billing_staff",
          "accountant",
        ],
      },
    ],
  },
  {
    title: "CLINICAL",
    items: [
      {
        to: "/opd",
        icon: ClipboardList,
        label: "OPD Queue",
        roles: ["hospital_admin", "owner", "doctor", "nurse", "receptionist"],
      },
      {
        to: "/appointments",
        icon: Calendar,
        label: "Appointments",
        roles: ["hospital_admin", "owner", "doctor", "nurse", "receptionist"],
      },
      {
        to: "/patients",
        icon: Users2,
        label: "Patient Directory",
        roles: ["hospital_admin", "doctor", "nurse", "receptionist"],
      },
      {
        to: "/ipd",
        icon: BedDouble,
        label: "IPD",
        roles: ["hospital_admin", "doctor", "nurse"],
      },
      {
        to: "/ot",
        icon: Scissors,
        label: "Operation Theatre",
        roles: ["hospital_admin", "owner", "doctor", "nurse"],
      },
      {
        to: "/pathology",
        icon: FlaskConical,
        label: "Pathology",
        roles: ["hospital_admin", "doctor", "lab_technician", "nurse"],
      },
      {
        to: "/radiology",
        icon: Scan,
        label: "Radiology",
        roles: ["hospital_admin", "doctor", "radiologist"],
      },
      {
        to: "/pharmacy",
        icon: Pill,
        label: "Pharmacy",
        roles: ["hospital_admin", "pharmacist", "doctor"],
      },
      {
        to: "/certificates",
        icon: Award,
        label: "Certificates",
        roles: ["hospital_admin", "owner", "doctor"],
      },
    ],
  },
  {
    title: "OPERATIONS",
    items: [
      {
        to: "/billing",
        icon: CreditCard,
        label: "Billing",
        roles: [
          "hospital_admin",
          "owner",
          "billing_staff",
          "accountant",
          "receptionist",
        ],
      },
      {
        to: "/credit-notes",
        icon: Receipt,
        label: "Credit Notes",
        roles: ["hospital_admin", "owner", "billing_staff", "accountant"],
      },
      {
        to: "/tpa-claims",
        icon: ShieldCheck,
        label: "TPA / Insurance",
        roles: ["hospital_admin", "owner", "billing_staff", "accountant"],
      },
      {
        to: "/billing-master",
        icon: IndianRupee,
        label: "Charge Master",
        roles: [
          "hospital_admin",
          "owner",
          "lab_technician",
          "radiologist",
          "pharmacist",
          "billing_staff",
          "accountant",
        ],
      },
      {
        to: "/packages",
        icon: Package,
        label: "Packages",
        roles: ["hospital_admin", "owner", "billing_staff", "accountant"],
      },
      {
        to: "/inventory",
        icon: ShoppingCart,
        label: "Inventory",
        roles: ["hospital_admin", "owner", "pharmacist"],
      },
      {
        to: "/reports",
        icon: BarChart2,
        label: "Reports",
        roles: ["hospital_admin", "owner", "accountant", "billing_staff"],
      },
    ],
  },
  {
    title: "ADMIN",
    items: [
      {
        to: "/users",
        icon: Users,
        label: "Users",
        roles: ["hospital_admin", "owner"],
      },
      {
        to: "/staff",
        icon: CalendarDays,
        label: "Staff & Roster",
        roles: ["hospital_admin", "owner"],
      },
      {
        to: "/settings",
        icon: Settings,
        label: "Settings",
        roles: ["hospital_admin", "owner"],
      },
      {
        to: "/print-settings",
        icon: Printer,
        label: "Print Headers",
        roles: ["hospital_admin", "owner"],
      },
    ],
  },
  {
    title: "ACCOUNT",
    items: [
      {
        to: "/profile",
        icon: UserCircle,
        label: "My Profile",
        roles: [
          "hospital_admin",
          "owner",
          "doctor",
          "nurse",
          "receptionist",
          "lab_technician",
          "pharmacist",
          "billing_staff",
          "accountant",
          "radiologist",
        ],
      },
      {
        to: "/subscription",
        icon: Receipt,
        label: "Subscription",
        roles: ["hospital_admin", "owner"],
      },
      {
        to: "/support",
        icon: LifeBuoy,
        label: "Support",
        roles: [
          "hospital_admin",
          "owner",
          "doctor",
          "nurse",
          "receptionist",
          "lab_technician",
          "pharmacist",
          "billing_staff",
          "accountant",
          "radiologist",
        ],
      },
      {
        to: "/sessions",
        icon: MonitorSmartphone,
        label: "Sessions",
        roles: [
          "hospital_admin",
          "owner",
          "doctor",
          "nurse",
          "receptionist",
          "lab_technician",
          "pharmacist",
          "billing_staff",
          "accountant",
          "radiologist",
        ],
      },
    ],
  },
];

function initials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}

export default function DashboardLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Global patient search (Ctrl+K)
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [menuFilter, setMenuFilter] = useState("");

  const role = user?.role ?? "";
  // Merge primary + additional roles for nav visibility
  const allRoles = [role, ...(user?.additionalRoles ?? [])];

  // Auto-logout after 30 min of inactivity for clinical roles (DPDP Act / PHI protection)
  const CLINICAL_ROLES = new Set([
    "doctor",
    "nurse",
    "receptionist",
    "lab_technician",
    "pharmacist",
    "radiologist",
  ]);
  const INACTIVITY_MS = 30 * 60 * 1000;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!CLINICAL_ROLES.has(role)) return;

    function resetTimer() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleLogout, INACTIVITY_MS);
    }

    const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    events.forEach((e) =>
      window.addEventListener(e, resetTimer, { passive: true }),
    );
    resetTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Ctrl+K → open search
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") setSearchOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleSearchInput = useCallback((q: string) => {
    setSearchQuery(q);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await patientApi.list({
          search: q.trim(),
          limit: 8,
          page: 1,
        });
        setSearchResults(res.data.data ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  function selectSearchResult(patient: any) {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    navigate(`/patients/${patient.id}`);
  }

  async function handleLogout() {
    try {
      await authApi.logout();
    } catch {}
    logout();
    navigate("/login");
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50 print:hidden">
      {/* ── Top header bar ─────────────────────────────────────────── */}
      <header className="h-11 bg-navy border-b border-white/10 flex items-center px-4 flex-shrink-0 z-20">
        {/* Mobile hamburger */}
        <button
          className="lg:hidden mr-3 text-white/50 hover:text-white transition-colors"
          onClick={() => setMobileOpen((o) => !o)}
        >
          <Menu size={18} />
        </button>

        {/* Brand */}
        <div className="flex items-center gap-2.5 flex-shrink-0 mr-4">
          <div className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center">
            <AppLogo size={15} inverted />
          </div>
          {/* Product name — always visible */}
          <span className="text-white/60 text-xs font-medium hidden sm:block select-none">
            Aarogya Saarthi
          </span>
          {/* Separator */}
          <span className="text-white/20 hidden sm:block select-none">|</span>
          {/* Tenant name — prominent so staff always know their workspace */}
          <span
            className="text-white text-sm font-semibold hidden sm:block truncate max-w-[200px]"
            title={user?.hospitalName ?? "HMIS"}
          >
            {user?.hospitalName ?? "HMIS"}
          </span>
        </div>

        {/* Global search */}
        <div className="flex-1 flex items-center relative">
          {searchOpen ? (
            <div className="w-full max-w-sm relative">
              <div
                className="flex items-center gap-2 bg-white/12 border border-white/25
                              rounded-md px-3 py-1"
              >
                <Search size={13} className="flex-shrink-0 text-white/50" />
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  placeholder="Search patients…"
                  className="flex-1 bg-transparent text-white text-xs outline-none
                             placeholder:text-white/35 min-w-0"
                  autoComplete="off"
                />
                {searchLoading && (
                  <div
                    className="w-3 h-3 border border-white/40 border-t-white/80
                                  rounded-full animate-spin flex-shrink-0"
                  />
                )}
                <button
                  onClick={() => {
                    setSearchOpen(false);
                    setSearchQuery("");
                    setSearchResults([]);
                  }}
                  className="text-white/40 hover:text-white flex-shrink-0"
                >
                  <X size={12} />
                </button>
              </div>
              {/* Results dropdown */}
              {searchResults.length > 0 && (
                <div
                  className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg
                                shadow-xl border border-gray-200 overflow-hidden z-50 max-h-72 overflow-y-auto"
                >
                  {searchResults.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => selectSearchResult(p)}
                      className="w-full text-left px-3 py-2.5 hover:bg-orange/8
                                 border-b border-gray-100 last:border-0 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-medium text-navy">
                            {p.name}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {p.patient_uid}
                            {p.age_years ? ` · ${p.age_years}y` : ""}
                            {p.gender
                              ? ` · ${p.gender === "male" ? "M" : p.gender === "female" ? "F" : "O"}`
                              : ""}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 flex-shrink-0">
                          {p.city ?? ""}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.trim() &&
                !searchLoading &&
                searchResults.length === 0 && (
                  <div
                    className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg
                                shadow-xl border border-gray-200 px-3 py-3 z-50 text-xs text-gray-400"
                  >
                    No patients found for "{searchQuery}"
                  </div>
                )}
            </div>
          ) : (
            <button
              onClick={() => {
                setSearchOpen(true);
                setTimeout(() => searchInputRef.current?.focus(), 50);
              }}
              className="flex items-center gap-2 bg-white/8 border border-white/10
                         hover:border-white/20 rounded-md px-3 py-1 text-sm text-white/40
                         transition-colors w-full max-w-sm"
            >
              <Search size={13} className="flex-shrink-0" />
              <span className="text-xs truncate">
                Search patients… (Ctrl+K)
              </span>
            </button>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
          <button className="relative text-white/40 hover:text-white transition-colors p-1">
            <Bell size={16} />
            <span className="absolute top-0 right-0 w-1.5 h-1.5 bg-orange rounded-full" />
          </button>

          <div className="h-4 w-px bg-white/15" />

          <Link
            to="/profile"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div
              className="w-6 h-6 rounded-full bg-orange/20 flex items-center justify-center
                            text-orange text-2xs font-bold select-none"
            >
              {initials(user?.name ?? "U")}
            </div>
            <div className="hidden lg:block">
              <div className="text-white/70 text-xs font-medium leading-tight truncate max-w-[120px]">
                {user?.name}
              </div>
              <div className="text-white/35 text-2xs capitalize leading-tight">
                {user?.role?.replace(/_/g, " ")}
              </div>
            </div>
          </Link>

          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-white/45 hover:text-white text-xs
                       px-2 py-1 rounded hover:bg-white/10 transition-colors"
          >
            <LogOut size={13} />
            <span className="hidden sm:block">Sign out</span>
          </button>
        </div>
      </header>

      {/* ── Body ───────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-10 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* ── Sidebar ──────────────────────────────────────────────── */}
        <aside
          className={clsx(
            "flex flex-col bg-[#1b2537] border-r border-white/5 transition-all duration-200 flex-shrink-0 z-10",
            "hidden lg:flex",
            collapsed ? "lg:w-14" : "lg:w-52",
            mobileOpen && "!flex fixed inset-y-0 left-0 w-52 top-11",
          )}
        >
          {/* Filter search */}
          <div
            className={clsx(
              "border-b border-white/5",
              collapsed ? "flex justify-center py-3 px-2" : "p-3",
            )}
          >
            {collapsed ? (
              <Search size={14} className="text-white/30" />
            ) : (
              <div
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md
                              bg-white/5 border border-white/8 hover:border-white/15 transition-colors"
              >
                <Search size={12} className="text-white/30 flex-shrink-0" />
                <input
                  value={menuFilter}
                  onChange={(e) => setMenuFilter(e.target.value)}
                  placeholder="Filter menu..."
                  className="bg-transparent text-white/60 text-xs outline-none w-full placeholder:text-white/25"
                />
              </div>
            )}
          </div>

          {/* Nav sections */}
          <nav className="flex-1 overflow-y-auto py-2">
            {ALL_NAV.map((section) => {
              // const visible = section.items.filter(i => i.roles.some(r => allRoles.includes(r)));
              const visible = section.items.filter((item) => {
                const hasRole = item.roles.some((r) => allRoles.includes(r));

                const matchesSearch =
                  item.label.toLowerCase().includes(menuFilter.toLowerCase()) ||
                  section.title
                    .toLowerCase()
                    .includes(menuFilter.toLowerCase());

                return hasRole && matchesSearch;
              });
              if (!visible.length) return null;
              return (
                <div key={section.title}>
                  {!collapsed && (
                    <div
                      className="px-3 pt-3 pb-1 text-2xs font-semibold text-white/25
                                    tracking-widest uppercase select-none"
                    >
                      {section.title}
                    </div>
                  )}
                  {visible.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      title={collapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        clsx(
                          "flex items-center gap-2.5 mx-2 px-2.5 py-2 rounded-md text-sm transition-colors",
                          collapsed && "justify-center mx-1 px-0",
                          isActive
                            ? "bg-orange/15 text-orange font-medium"
                            : "text-white/50 hover:text-white hover:bg-white/6",
                        )
                      }
                    >
                      <item.icon size={15} className="flex-shrink-0" />
                      {!collapsed && (
                        <span className="text-[13px]">{item.label}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </nav>

          {/* Collapse toggle */}
          <div className="border-t border-white/5 p-2">
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="flex items-center justify-center w-full py-1.5 rounded
                         text-white/25 hover:text-white/60 hover:bg-white/5 transition-colors"
            >
              {collapsed ? (
                <ChevronRight size={13} />
              ) : (
                <ChevronLeft size={13} />
              )}
            </button>
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────────────────── */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
